import { useEffect, useState, RefObject } from 'react'

/**
 * Intersection observer threshold values for visibility detection.
 * Multiple thresholds to catch any level of visibility.
 */
const VISIBILITY_THRESHOLDS = [0, 0.01, 0.05, 0.1, 0.2, 0.3, 0.4, 0.5]

/** Minimum intersection ratio to consider element visible */
const MIN_INTERSECTION_RATIO = 0.01

/**
 * Hook to track visibility of an element using Intersection Observer.
 * Useful for showing/hiding mobile sticky footers based on section visibility.
 *
 * @param ref - React ref to the element to observe
 * @param isElementMounted - When true, the observed element is in the DOM (e.g. after loading). Pass this so the observer is attached when the section actually renders (avoids ref.current being null on first effect run).
 * @returns Boolean indicating if element is visible (at least 1% in viewport)
 *
 * @example
 * ```typescript
 * const sectionRef = useRef<HTMLElement>(null)
 * const isVisible = usePreviewVisibility(sectionRef, !loading && !!data)
 * ```
 */
export function usePreviewVisibility(
	ref: RefObject<HTMLElement | null>,
	isElementMounted: boolean = true
): boolean {
	const [isVisible, setIsVisible] = useState(false)

	useEffect(() => {
		if (!isElementMounted) return

		const currentElement = ref.current
		if (!currentElement) return

		const observer = new IntersectionObserver(
			([entry]) => {
				// Show when any part is visible and at least 1% is visible
				const shouldShow =
					entry.isIntersecting && entry.intersectionRatio >= MIN_INTERSECTION_RATIO
				setIsVisible(shouldShow)
			},
			{
				threshold: VISIBILITY_THRESHOLDS,
				rootMargin: '0px'
			}
		)

		observer.observe(currentElement)

		return () => {
			observer.unobserve(currentElement)
		}
	}, [isElementMounted, ref])

	return isVisible
}
